// Declaration PDF preview for one Doc Pabean row (revamp PR 7a). Same request, headers, abort/blob handling and billing
// error path as the Mantine modal it replaces; the dialog is a Lumen Dialog (full screen on phones).
import axios from 'axios';
import { Download, ExternalLink, FileText, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/lumen/alert';
import { Badge } from '@/components/lumen/badge';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { cn } from '@/lib/utils';
import { formatTemplate } from '@/lib/format-template';
import { useLanguage } from '@contexts/LanguageContext';
import axiosInstance from '@libs/axios';
import { cleanAjuRaw, formatAjuNumber } from '@utils/aju-formatter';
import { handleCreditBlockError } from '@utils/credit-errors';
import { useIsMobile } from './use-is-mobile';

type DocumentKind = 'final' | 'draft';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading'; refresh: boolean }
  | { status: 'ready'; url: string; kind: DocumentKind; fallback: boolean; fileName: string }
  | { status: 'error'; message: string };

interface DeclarationPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  /** AJU of the row being previewed. Null while nothing is selected. */
  ajuNo: string | null;
}

// Axios hands us a Blob for every response when responseType is 'blob', including
// error bodies. Read it back as JSON so the CEISA message (and the billing error
// code) reach the user instead of a bare "Request failed with status code 400".
/** An API error envelope: `{ error: { code, message } }` or `{ message }`. */
type ErrorBody = { error?: { code?: string; message?: string }; message?: string };
/** What an axios error carries that this modal reads. */
type RequestError = { response?: { status?: number; data?: unknown }; message?: string };

const readBlobJson = async (data: unknown): Promise<ErrorBody | null> => {
  // A real PDF (or anything too large to be an error envelope) is left alone.
  if (!(data instanceof Blob) || data.type === 'application/pdf' || data.size > 64 * 1024) return null;
  try {
    const text = await data.text();
    if (!text.trim()) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const blobErrorMessage = (parsed: ErrorBody | null): string | null => parsed?.error?.message || parsed?.message || null;

// Blob URLs the user popped out with "Open in New Tab". That tab still reads
// from the URL, so closing the modal or loading another AJU must not revoke it;
// the browser frees it when this document unloads.
const detachedBlobUrls = new Set<string>();

const fileNameFromDisposition = (header: unknown): string | null => {
  if (typeof header !== 'string') return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Declaration PDF (PIB / PEB) for one Dok Pabean row, without opening the draft.
 *
 * The backend decides which document to serve: the registered (NOPEN) form once
 * the AJU has ever had an NPE / SPPB, otherwise the CEISA draft. It says which
 * one it sent in X-Document-Kind, and X-Document-Fallback when the final could
 * not be fetched and the draft stood in for it.
 *
 * The PDF goes into an iframe over a blob URL so the browser's own viewer
 * provides zoom, thumbnails and print. Every request carries an AbortController:
 * closing the modal, or opening it on another row, cancels the one in flight so
 * a slow CEISA render can never land under the wrong AJU.
 */
export default function DeclarationPreviewModal({ opened, onClose, ajuNo }: DeclarationPreviewModalProps) {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [state, setState] = useState<PreviewState>({ status: 'idle' });
  const controllerRef = useRef<AbortController | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const cleanAju = ajuNo ? cleanAjuRaw(ajuNo) : '';
  // BC 2.0 / 3.0 are the only prefixes the spec names; TPB rows (000023, 000027,
  // 000040, …) share the list, so they get a neutral label rather than "PIB".
  const docKind = cleanAju.startsWith('000020') ? 'PIB' : cleanAju.startsWith('000030') ? 'PEB' : null;
  const docLabel = docKind ?? t('docpabean.pdfPreview.docGeneric');
  const fileLabel = docKind ?? 'DOKUMEN';

  const releaseBlobUrl = useCallback(() => {
    const url = blobUrlRef.current;
    blobUrlRef.current = null;
    if (url && !detachedBlobUrls.has(url)) {
      window.URL.revokeObjectURL(url);
    }
  }, []);

  const load = useCallback(
    async (refresh: boolean) => {
      if (!cleanAju) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState({ status: 'loading', refresh });

      try {
        const response = await axiosInstance.get(
          `/api/v1/ceisa/uploads/declaration-preview/${encodeURIComponent(cleanAju)}`,
          {
            params: refresh ? { refresh: true } : undefined,
            responseType: 'blob',
            signal: controller.signal,
          }
        );

        // A 200 carrying a JSON error body would otherwise render as a broken PDF.
        const softError = blobErrorMessage(await readBlobJson(response.data));
        if (controller.signal.aborted) return;
        if (softError) {
          setState({ status: 'error', message: softError });
          return;
        }

        const kind: DocumentKind =
          String(response.headers['x-document-kind'] ?? '').toLowerCase() === 'final' ? 'final' : 'draft';
        const fallback = String(response.headers['x-document-fallback'] ?? '').toLowerCase() === 'true';
        const fileName =
          fileNameFromDisposition(response.headers['content-disposition']) ||
          (kind === 'final'
            ? `${fileLabel}-NOPEN-${cleanAju}.pdf`
            : `draft-${fileLabel.toLowerCase()}-${cleanAju}.pdf`);

        releaseBlobUrl();
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        blobUrlRef.current = url;
        setState({ status: 'ready', url, kind, fallback, fileName });
      } catch (caught) {
        if (controller.signal.aborted || axios.isCancel(caught)) return;
        const error = caught as RequestError;
        const body = error?.response?.data as ErrorBody | undefined;

        // Error bodies arrive as Blobs too; parse them so handleCreditBlockError
        // can see the structured error code, not just the HTTP status.
        const parsed = await readBlobJson(error?.response?.data);
        if (controller.signal.aborted) return;
        const normalized = parsed ? { ...error, response: { ...error.response, data: parsed } } : error;

        if (handleCreditBlockError(normalized)) {
          // The persistent billing toast carries the "Bayar Sekarang" link; the
          // modal stays open and says why the preview is empty.
          setState({ status: 'error', message: t('docpabean.pdfPreview.errorBilling') });
          return;
        }

        const message =
          blobErrorMessage(parsed) ||
          body?.error?.message ||
          body?.message ||
          error?.message ||
          t('docpabean.pdfPreview.errorGeneric');
        setState({ status: 'error', message });
      }
    },
    [cleanAju, fileLabel, releaseBlobUrl, t]
  );

  useEffect(() => {
    if (!opened || !cleanAju) return;
    void load(false);
    // State is left as is here: resetting it now would swap the PDF for the loader
    // while the modal fades out. load() moves straight to 'loading' on the next open.
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      releaseBlobUrl();
    };
    // `load` is keyed on the AJU already; re-running on its identity alone (e.g. a
    // language switch changing `t`) would refetch a PDF that is already on screen.
  }, [opened, cleanAju]);

  // Unmount with the modal still open (e.g. navigating away).
  useEffect(
    () => () => {
      controllerRef.current?.abort();
      releaseBlobUrl();
    },
    [releaseBlobUrl]
  );

  const handleDownload = () => {
    if (state.status !== 'ready') return;
    const link = document.createElement('a');
    link.href = state.url;
    link.setAttribute('download', state.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenInNewTab = () => {
    if (state.status !== 'ready') return;
    detachedBlobUrls.add(state.url);
    window.open(state.url, '_blank', 'noopener,noreferrer');
  };

  const isLoading = state.status === 'loading';
  const ready = state.status === 'ready' ? state : null;

  const kindBadge = ready && (
    <Badge variant={ready.kind === 'final' ? 'ok' : 'warn'} className="shrink-0">
      {ready.kind === 'final' ? t('docpabean.pdfPreview.kindFinal') : t('docpabean.pdfPreview.kindDraft')}
    </Badge>
  );
  const spinner = <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />;

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // Clear the last document once the close animation is over, so the next open never paints a stale badge or a
        // revoked blob URL before load() runs.
        onAnimationEnd={() => {
          if (!opened) setState({ status: 'idle' });
        }}
        className={cn(
          'flex flex-col gap-3 sm:max-w-[min(90rem,calc(100%-4rem))]',
          // The flex chain hands the PDF frame whatever is left after the header, the fallback alert and the buttons.
          isMobile ? 'h-dvh max-h-dvh w-screen max-w-none rounded-none' : 'h-[90dvh]'
        )}
      >
        <DialogHeader className="border-line border-b pb-3">
          <DialogTitle className="flex min-w-0 items-center gap-2">
            <FileText aria-hidden className="size-[18px] shrink-0 text-err" />
            <span className="grid min-w-0">
              <span className="truncate">{formatTemplate(t('docpabean.pdfPreview.title'), { doc: docLabel })}</span>
              {cleanAju && (
                <span className="truncate font-mono font-normal text-fg-muted text-xs tabular-nums">
                  {formatAjuNumber(cleanAju)}
                </span>
              )}
            </span>
            {kindBadge}
          </DialogTitle>
        </DialogHeader>

        {ready?.fallback && (
          <Alert variant="warn">
            <TriangleAlert />
            <AlertTitle>{t('docpabean.pdfPreview.fallbackTitle')}</AlertTitle>
            <AlertDescription>{t('docpabean.pdfPreview.fallbackBody')}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {(ready?.kind === 'draft' || (state.status === 'loading' && state.refresh)) && (
            <Button size="sm" variant="outline" onClick={() => load(true)} disabled={isLoading}>
              {isLoading ? spinner : <RefreshCw aria-hidden />}
              {t('docpabean.pdfPreview.refreshDraft')}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleOpenInNewTab} disabled={!ready}>
            <ExternalLink aria-hidden />
            {t('docpabean.pdfPreview.openNewTab')}
          </Button>
          <Button size="sm" onClick={handleDownload} disabled={!ready}>
            <Download aria-hidden />
            {t('docpabean.pdfPreview.download')}
          </Button>
        </div>

        <div
          className={cn(
            'flex flex-1 overflow-hidden rounded-card border border-line bg-surface-2',
            isMobile ? 'min-h-0' : 'min-h-60'
          )}
        >
          {ready ? (
            <iframe
              key={ready.url}
              title={formatTemplate(t('docpabean.pdfPreview.title'), { doc: docLabel })}
              src={ready.url}
              className="w-full flex-1 border-0 bg-white" // a PDF page is white in both modes
            />
          ) : state.status === 'error' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
              <TriangleAlert aria-hidden className="size-7 text-err" />
              <p className="font-semibold text-fg text-sm">{t('docpabean.pdfPreview.errorTitle')}</p>
              <p className="max-w-[480px] text-fg-muted text-xs">{state.message}</p>
              <Button size="sm" variant="outline" onClick={() => load(false)}>
                <RefreshCw aria-hidden />
                {t('docpabean.pdfPreview.retry')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
              <Loader2 aria-hidden className="size-7 animate-spin text-label motion-reduce:animate-none" />
              <p className="font-semibold text-fg text-sm">{t('docpabean.pdfPreview.loading')}</p>
              <p className="max-w-[420px] text-fg-muted text-xs">{t('docpabean.pdfPreview.loadingHint')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
